import React, { useState, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TiDeleteOutline } from "react-icons/ti";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../utils/toast";
import ButtonUI from "../material-ui/ButtonUI";
import { AuthContext } from "../../context/Auth/AuthContext";
import editDocument from "../../api/editDocument";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import "../../styles/RejectCasePopup.css";
import { BiLinkAlt } from "react-icons/bi";

// Helper to normalize extensions (jpg and jpeg to jpeg)
const normalizeExt = (ext = "") => {
  const e = (ext || "").toLowerCase();
  if (e === "jpg") return "jpeg";
  if (e === "jfif") return "jpeg";
  if (e === "htm") return "html";
  if (e === "tif") return "tiff";
  if (e === "doc") return "docx";
  return e;
};

// Validate file types
const isValidFileType = (extension) => {
  const validExtensions = ["pdf", "jpeg", "png", "doc", "docx"]; // Normalize to 'jpeg' instead of 'jpg'
  return validExtensions.includes(normalizeExt(extension)); // Normalize the extension here
};

const rejectCaseFormSchema = yup.object().shape({
  addRequestName: yup.string().required("Request List is required field"),
});

const LinkToRequest = ({
  documentId,
  requestOptions,
  setLinkRequest,
  setReqStatus,
  setRequestId,
  setLinkRequestOpen,
  currentTabId,
  closeTabById,
  setSelectedRequestLabel,
  setIsBlocking,
  setDocumentViewer,
  setDocumentUpdatedToTrue,
  pdfLink,
  documentRequestId,
}) => {
  const [open, setOpen] = useState(true);
  const { token, user_id } = useContext(AuthContext);

  // Extract the file extension and normalize it
  const fileExtension = normalizeExt(pdfLink.split(".").pop().toLowerCase());

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(rejectCaseFormSchema),
    defaultValues: {
      addRequestName: documentRequestId || "",
    },
  });

  const filterOptionsLinkList = createFilterOptions({
    matchFrom: "any",
    ignoreCase: true, // Ignore case sensitivity
    stringify: (option) => option.document_req_name,
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLinkRequestOpen(false);
  };

  const onLinkRequestSubmit = async (addDocumentFormData) => {
    if (setIsBlocking) {
      setIsBlocking(true);
    }

    let isSuccess = false;
    let selectedRequestName = "";
    let linkRequestResponse;
    try {
      linkRequestResponse = await editDocument({
        token: token,
        user_id: user_id,
        document_id: documentId,
        document_request_id: addDocumentFormData.addRequestName,
      });

      if (linkRequestResponse?.code === 200) {
        setReqStatus("Uploaded");
        if (setRequestId) {
          setRequestId(linkRequestResponse?.data?.document_request_id);
        }
        setLinkRequest(true);
        isSuccess = true;
        selectedRequestName = requestOptions.find(
          (option) =>
            option.document_request_id === addDocumentFormData.addRequestName
        ).document_req_name;

        setSelectedRequestLabel(selectedRequestName);
        setOpen(false);

        setLinkRequestOpen(false);
        if (currentTabId && linkRequestResponse?.combinedDoc) {
          closeTabById(currentTabId);
        }
      } else {
        throw new Error("Link request failed");
      }
    } catch (error) {
      setOpen(false);
      setLinkRequest(false);
      setLinkRequestOpen(false);
      toast.error(
        linkRequestResponse?.message ||
          "Error while linking the document to the request",
        {
          position: "top-center",
          autoClose: 5000,
          theme: "dark",
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      if (setIsBlocking) {
        setIsBlocking(false);
      }
    } finally {
      if (isSuccess) {
        if (linkRequestResponse?.combinedDoc) {
          showSuccess("Document is merged for the link request successfully");
          if (setDocumentViewer) {
            setDocumentViewer(false);
          }
          if (setDocumentUpdatedToTrue) {
            setDocumentUpdatedToTrue();
          }
        } else {
          showSuccess("Document is linked with request successfully");
        }

        if (setIsBlocking) {
          setTimeout(() => {
            setIsBlocking(false);
          }, 2000); // shorter delay if needed
        }
      }
    }
  };

  return (
    <div className="dialog-modal">
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" className="edit-doc-modal">
          <div className="popup-header-svg-icon">
            <TiDeleteOutline />
          </div>
          <div className="popup-header-title">Link to Request List</div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <FormControl className="" fullWidth>
                <Controller
                  control={control}
                  name="addRequestName"
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      options={requestOptions}
                      filterOptions={filterOptionsLinkList}
                      getOptionLabel={(option) =>
                        option.document_req_name || ""
                      }
                      getOptionKey={(option) =>
                        option.document_request_id || ""
                      }
                      onChange={(e, value) => {
                        onChange(value ? value.document_request_id : "");
                      }}
                      value={
                        requestOptions?.find(
                          (option) => option.document_request_id === value
                        ) || null
                      }
                      renderOption={(props, option) => {
                        // Normalize the document type to compare it with the fileExtension
                        const normalizedDocType = normalizeExt(
                          option.document_type
                        );

                        // Check if the file type is valid and if the document type matches
                        const isValidType = isValidFileType(fileExtension);

                        // Check if option is_present === 1 and the file type is valid
                        if (
                          (option.is_present === 1 && !isValidType) ||
                          (option.is_present === 1 &&
                            normalizedDocType !== fileExtension)
                        ) {
                          // Return null to completely hide the item
                          return null;
                        }

                        return (
                          <li
                            {...props}
                            key={option.document_request_id}
                            style={{
                              ...props.style,
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: option.document_request_id === documentRequestId ? "#e3f2fd" : props.style?.backgroundColor,
                            }}
                          >
                            {/* If the file type is valid and option.is_present === 1, display the BiLinkAlt icon */}
                            {option.is_present === 1 && isValidType && (
                              <BiLinkAlt
                                style={{
                                  color: "#1976d2", // blue link
                                  fontSize: 20,
                                  marginRight: "8px", // space between the icon and label
                                }}
                              />
                            )}
                            <span style={{ flex: 1 }}>
                              {option.document_req_name}
                            </span>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Request List"
                          variant="outlined"
                        />
                      )}
                      sx={{ paddingTop: "10px" }}
                    />
                    // <Autocomplete
                    //   options={requestOptions}
                    //   filterOptions={filterOptionsLinkList}
                    //   getOptionKey={(option) =>
                    //     option.document_request_id || ""
                    //   }
                    //   getOptionLabel={(option) =>
                    //     option.document_req_name || ""
                    //   }
                    //   renderInput={(params) => (
                    //     <TextField
                    //       {...params}
                    //       label="Request List"
                    //       variant="outlined"
                    //     />
                    //   )}
                    // />
                  )}
                />
              </FormControl>
              {/* <FormControl className="reject-doc-select-ui">
                <InputLabel
                  className="reject-doc-select-label"
                  id="select-label"
                >
                  Request List
                </InputLabel>
                <Controller
                  control={control}
                  name="addRequestName"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="addRequestName"
                      sx={{ margin: "10px 0px" }}
                      labelId="select-label"
                      variant="outlined"
                      id="simple-select"
                      value={value}
                      onChange={onChange}
                      label="Request List"
                      {...register("addRequestName")}
                    >
                      {requestOptions.map((option) => (
                        <MenuItem
                          key={option.document_request_id}
                          value={option.document_request_id}
                        >
                          {option.document_req_name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl> */}
              <span className="error-message">
                {errors["addRequestName"]?.message}
              </span>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ justifyContent: "flex-end" }}>
          <ButtonUI
            formButton={false}
            variant="outlined"
            buttonText="No"
            style={{
              margin: "15px 0px",
              marginTop: "0px",
              backgroundColor: "#fff",
              color: "#1a6b7c",
              border: "1px solid #1a6b7c",
            }}
            onClickFunc={handleClose}
          />
          <ButtonUI
            variant="outlined"
            buttonText="Yes"
            style={{
              margin: "15px",
              marginTop: "0px",
              backgroundColor: "#fff",
              color: "##1a6b7c",
              border: "1px solid #1a6b7c",
            }}
            autoFocus={true}
            handleSubmit={handleSubmit}
            onFormSubmit={onLinkRequestSubmit}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LinkToRequest;
